import * as Radix from '@radix-ui/react-toggle-group'
import { cn } from '../../lib/cn.js'

export interface ToggleOption {
  value: string
  label: React.ReactNode
  disabled?: boolean
  /**
   * The name, when the label is an icon.
   *
   * A segmented control of three glyphs is three buttons called nothing
   * without it.
   */
  srLabel?: string
}

const chip = cn(
  /* `h-9`, not `py-1`. These came out 30px, which is shorter than every
     other control on the same form and reads as decoration rather than as
     something to press. The small control size on this desk is 36. */
  'inline-flex h-9 items-center rounded-full border px-4 text-second transition-colors outline-none',
  'border-hairline-strong bg-surface text-ink-2 hover:bg-sunk',
  'focus-visible:ring-2 focus-visible:ring-coral-fill/30',
  'data-[state=on]:border-coral-fill data-[state=on]:bg-coral-soft data-[state=on]:text-coral-text',
  'disabled:opacity-50 disabled:pointer-events-none',
)

/**
 * A set of chips where one — or several — are chosen.
 *
 * 9.6a's *"the type as four chips — Masterclass, Workshop, Live clinic,
 * One-to-one"*. The screen used to carry a button called **Change the type** that
 * named the four choices in a toast and offered none of them; the fix is that the
 * choices are the control.
 *
 * `Chip` is the single pressable pill. This is the *set*, and it owns which is on.
 */
/**
 * Joined, for a short set of icons standing for one setting.
 *
 * Chips are right where the choices are words and there may be several —
 * four class types wrap onto two lines and that is fine. They are wrong for
 * *light / dark / follow my computer*, which is one setting with three
 * readings: three separated pills read as three things, and a joined track
 * with the chosen segment filled reads as one control with a position.
 */
const segment = cn(
  'inline-flex h-7 flex-1 items-center justify-center rounded-sm transition-colors outline-none',
  'text-ink-2 hover:text-ink',
  'focus-visible:ring-2 focus-visible:ring-coral-fill/30',
  'data-[state=on]:bg-surface data-[state=on]:text-coral-text data-[state=on]:shadow-e1',
  'disabled:opacity-50 disabled:pointer-events-none',
  '[&_svg]:size-4',
)

type GroupProps = {
  options: ToggleOption[]
  className?: string
  /** `chips` by default. See `segment` for when the other one is right. */
  variant?: 'chips' | 'segmented'
} & React.ComponentPropsWithoutRef<typeof Radix.Root>

export function ToggleGroup({ options, className, variant = 'chips', ...props }: GroupProps) {
  const segmented = variant === 'segmented'
  return (
    <Radix.Root
      className={cn(
        segmented
          ? 'bg-sunk border-hairline flex gap-0.5 rounded-md border p-0.5'
          : 'flex flex-wrap gap-2',
        className,
      )}
      {...(props as React.ComponentPropsWithoutRef<typeof Radix.Root>)}
    >
      {options.map((o) => (
        <Radix.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          {...(o.srLabel ? { 'aria-label': o.srLabel, title: o.srLabel } : {})}
          className={segmented ? segment : chip}
        >
          {o.label}
        </Radix.Item>
      ))}
    </Radix.Root>
  )
}
